import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLoginFunctions from './useLoginFunctions';
import { validateEmail } from '../../utils/validators';

import { PreAuthContent, InputBox, Button } from '../../components/new';
import St from './LoginScreen.module.css';

const LoginScreen = ({ history }) => {
  const { t } = useTranslation();
  const {
    showPassword,
    setShowPassword,
    rememberDevice,
    setRememberDevice,
    totpCode,
    setTotpCode,
    totpError,
    totpLoading,
    totpQR,
    totpSecret,
    isLoading,
    onTotpSubmit,
    onLoginHandler,
    userLogin,
    search,
    email,
    setEmail,
    password,
    setPassword,
    tokenPassword,
    setTokenPassword,
  } = useLoginFunctions(history);

  return (
    <PreAuthContent pageTitle={t('screens.loginExt.pageTitleLogin')} formGreeting={t('screens.loginExt.formGreetingLogin')} formTitle={t('screens.loginExt.formTitleLogin')}>
      {/* ── TOTP verification (already enrolled) ── */}
      {userLogin?.userInfo?.status === 'totp_required' && (
        <form onSubmit={onTotpSubmit}>
          <InputBox
            label={t('screens.loginExt.totpLabel')}
            subLabel={t('screens.loginExt.totpSubLabel')}
            type='text'
            inputMode='numeric'
            maxLength={6}
            placeholder={t('screens.loginExt.totpPlaceholder')}
            autoFocus
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            errorMessage={totpError || userLogin?.error}
          />

          <Button loading={totpLoading} disabled={totpCode.length !== 6 || totpLoading} type='submit' label={totpLoading ? '' : t('screens.loginExt.verify')} />
        </form>
      )}

      {/* ── TOTP enrollment (first-time setup) ── */}
      {userLogin?.userInfo?.status === 'totp_enrollment_required' && (
        <form onSubmit={onTotpSubmit}>
          <InputBox
            label={t('screens.loginExt.totpEnrollLabel')}
            subLabel={t('screens.loginExt.totpEnrollSubLabel')}
            loadingText={totpLoading ? t('screens.loginExt.loadingQr') : ''}
            image={totpQR}
            imageAlt={t('screens.loginExt.qrAlt')}
            imageKey={totpSecret}
            type='text'
            inputMode='numeric'
            maxLength={6}
            className={St.fieldInput}
            placeholder={t('screens.loginExt.totpPlaceholder')}
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            errorMessage={totpError || userLogin?.error}
          />

          <Button loading={totpLoading} disabled={totpCode.length !== 6 || totpLoading || !totpQR} type='submit' label={totpLoading ? '' : t('screens.loginExt.activate2fa')} />
        </form>
      )}

      {/* ── Normal login form ── */}
      {!userLogin?.userInfo?.status || userLogin?.userInfo?.status === 'success' || userLogin?.userInfo?.status === 'fail' ? (
        <form onSubmit={onLoginHandler}>
          {!search ? (
            <Fragment>
              {/* Email field */}
              <InputBox
                label={t('screens.loginExt.emailLabel')}
                id='rg-email'
                type='email'
                placeholder={t('screens.loginExt.emailPlaceholder')}
                autoComplete='email'
                value={email.value}
                onChange={e => {
                  const v = e.target.value;
                  setEmail({ value: v, error: '', validated: validateEmail(v) });
                }}
                errorMessage={email.error}
                icon='email-new'
              />

              {/* Password field */}
              <InputBox
                label={t('screens.loginExt.passwordLabel')}
                additionalClass={St.fieldSecond}
                id='rg-password'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('screens.loginExt.passwordPlaceholder')}
                autoComplete='current-password'
                value={password.value}
                onChange={e => {
                  const v = e.target.value;
                  setPassword({ value: v, error: '', validated: v.length >= 8 });
                }}
                errorMessage={password.error}
                icon='password-new'
                toggleIcon={showPassword ? 'eye-open' : 'eye-closed'}
                onPasswordToggle={() => setShowPassword(p => !p)}
                ariaLabelToggle={showPassword ? t('screens.loginExt.hidePassword') : t('screens.loginExt.showPassword')}
              />

              {/* Remember + Forgot */}
              <div className={St.formRow}>
                <label className={St.checkLabel}>
                  <input type='checkbox' className={St.checkBox} checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} />
                  {t('screens.loginExt.rememberDevice')}
                </label>
                <Link to='/forgot-password' className={St.forgotLink}>
                  {t('screens.loginExt.forgotPassword')}
                </Link>
              </div>

              <Button type='submit' loading={isLoading} disabled={isLoading} label={isLoading ? '' : t('screens.loginExt.signIn')} />
            </Fragment>
          ) : (
            /* Token-based login — password only */
            <Fragment>
              <InputBox
                label={t('screens.loginExt.passwordLabel')}
                additionalClass={St.fieldSecond}
                id='rg-token-password'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('screens.loginExt.passwordPlaceholder')}
                autoComplete='current-password'
                value={tokenPassword.value}
                onChange={e => {
                  const v = e.target.value;
                  setTokenPassword({ value: v, error: '', validated: v.length >= 8 });
                }}
                errorMessage={tokenPassword.error}
                icon='password-new'
                toggleIcon={showPassword ? 'eye-open' : 'eye-closed'}
                onPasswordToggle={() => setShowPassword(p => !p)}
                ariaLabelToggle={t('screens.loginExt.togglePassword')}
              />
              <Button type='submit' loading={isLoading} disabled={isLoading} label={isLoading ? '' : t('screens.loginExt.signIn')} />
            </Fragment>
          )}
        </form>
      ) : null}

      <div className={St.formFoot}>
        <p>
          {t('screens.loginExt.protectedNote')}
          <br />
          <a href='/#/privacy' target='_blank' rel='noopener noreferrer'>
            {t('screens.loginExt.privacyLink')}
          </a>
          &ensp;·&ensp;
          <a href='/#/terms' target='_blank' rel='noopener noreferrer'>
            {t('screens.loginExt.termsLink')}
          </a>
        </p>
        <div className={St.secureNote}>
          <svg viewBox='0 0 24 24' fill='#48a87c'>
            <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5zm-1 14l-4-4 1.41-1.41L11 13.17l5.59-5.59L18 9l-7 7z' />
          </svg>
          {t('screens.loginExt.tlsNote')}
        </div>
      </div>
    </PreAuthContent>
  );
};

export default LoginScreen;
